'use client';

import { Eye, EyeOff, Link2, FileEdit } from 'lucide-react';

interface PreviewBannerProps {
  visibility?: 'public' | 'preview' | 'unlisted' | 'hidden';
  status?: string;
  className?: string;
}

/**
 * Banner displayed on articles that are in preview/unlisted mode
 * Shows a clear indicator that this content is not yet publicly available
 */
export function PreviewBanner({ visibility, status, className = '' }: PreviewBannerProps) {
  // Don't show banner for public visibility with published status
  if ((!visibility || visibility === 'public') && status === 'published') {
    return null;
  }

  // Determine the message based on visibility and status
  let label = '';
  let message = '';
  let icon = <Eye className="w-4 h-4" />;
  let gradientClass = 'from-amber-500 to-amber-600';
  let borderColor = 'border-amber-600/30';

  if (visibility === 'preview') {
    label = 'PREVIEW';
    message = 'This article is not yet published. Only people with the link can see it.';
    icon = <Eye className="w-4 h-4" />;
    gradientClass = 'from-amber-500 to-orange-500';
    borderColor = 'border-amber-600/30';
  } else if (visibility === 'unlisted') {
    label = 'UNLISTED';
    message = 'This article is not publicly listed but can be accessed via direct link.';
    icon = <Link2 className="w-4 h-4" />;
    gradientClass = 'from-sky-500 to-blue-600';
    borderColor = 'border-sky-600/30';
  } else if (status !== 'published') {
    // Draft or other non-published status with public visibility
    label = 'DRAFT';
    message = 'This article has not been published yet.';
    icon = <FileEdit className="w-4 h-4" />;
    gradientClass = 'from-slate-500 to-slate-600';
    borderColor = 'border-slate-600/30';
  }

  if (!label) return null;

  return (
    <div 
      className={`bg-gradient-to-r ${gradientClass} text-white ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-2.5">
        <div className="flex items-center justify-center gap-3 text-sm">
          <div className={`flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur-sm border ${borderColor}`}>
            {icon}
            <span className="font-bold tracking-wide">{label}</span>
          </div>
          <span className="hidden sm:inline text-white/90 font-medium">—</span>
          <span className="hidden sm:inline text-white/90">{message}</span>
        </div>
      </div>
    </div>
  );
}

export default PreviewBanner;
